// pages/Dashboard/Attendance.js
import { useState, useEffect, useCallback } from 'react';
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaUserTie,
  FaSearch,
  FaUmbrellaBeach,
} from 'react-icons/fa';
import '../Css/Attendance.css';

import { useToast } from '../../components/Toast';
import { baseurl } from '../Baseurl';

const ROLE_STYLES = {
  Stylist: { cls: 'role-stylist' },
  Beautician: { cls: 'role-beautician' },
  'Makeup Artist': { cls: 'role-makeup' },
  'Massage Therapist': { cls: 'role-massage' },
  'Nail Technician': { cls: 'role-nails' },
  Receptionist: { cls: 'role-reception' },
  Manager: { cls: 'role-manager' },
};

const STATUS_OPTIONS = [
  { key: 'present', label: 'Present', cls: 'status-present', icon: <FaCheckCircle /> },
  { key: 'absent', label: 'Absent', cls: 'status-absent', icon: <FaTimesCircle /> },
  { key: 'half-day', label: 'Half Day', cls: 'status-halfday', icon: <FaClock /> },
  { key: 'leave', label: 'Leave', cls: 'status-leave', icon: <FaUmbrellaBeach /> },
];

const initials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');

const todayStr = () => new Date().toISOString().slice(0, 10);

function Attendance() {
  const [staffList, setStaffList] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [stats, setStats] = useState({ present: 0, absent: 0, halfDay: 0, leave: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedDate, setSelectedDate] = useState(todayStr());

  const toast = useToast();

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  });

  const fetchStaff = useCallback(async () => {
    const res = await fetch(`${baseurl}/staff`, { headers: authHeaders() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch staff');
    return data.data.staff;
  }, []);

  const fetchAttendance = useCallback(async (date) => {
    const res = await fetch(`${baseurl}/attendance?date=${date}`, {
      headers: authHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch attendance');
    return data.data;
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [staff, attendance] = await Promise.all([
        fetchStaff(),
        fetchAttendance(selectedDate),
      ]);

      setStaffList(staff);

      const map = {};
      (attendance.records || []).forEach((r) => {
        map[r.staff_id] = r.status;
      });
      setAttendanceMap(map);
      setStats(
        attendance.stats || { present: 0, absent: 0, halfDay: 0, leave: 0 }
      );
    } catch (err) {
      setError(err.message);
      toast(err.message || 'Failed to load attendance', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, fetchStaff, fetchAttendance]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleMark = async (staffId, status) => {
    const prevStatus = attendanceMap[staffId];
    setAttendanceMap((prev) => ({ ...prev, [staffId]: status }));
    setSaving(true);
    try {
      const res = await fetch(`${baseurl}/attendance/mark`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          staffId,
          date: selectedDate,
          status,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to mark attendance');

      toast(data.message || 'Attendance updated', 'success');
      if (data.data?.stats) setStats(data.data.stats);
    } catch (err) {
      setAttendanceMap((prev) => ({ ...prev, [staffId]: prevStatus }));
      setError(err.message);
      toast(err.message || 'Failed to mark attendance', 'error');
    } finally {
      setSaving(false);
    }
  };

  const filteredStaff = staffList.filter((m) =>
    m.name.toLowerCase().includes(search.toLowerCase()) ||
    m.employee_code?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="attendance-page">
      <div className="attendance-header">
        <div>
          <h2>Attendance</h2>
          <p>Mark and track daily staff attendance</p>
        </div>
        <div className="date-picker-group">
          <FaCalendarAlt className="input-icon" />
          <input
            type="date"
            className="date-input"
            value={selectedDate}
            max={todayStr()}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>
      </div>

      {error && <div className="attendance-error-banner">{error}</div>}

      <div className="attendance-stats-strip">
        <div className="attendance-stat-card">
          <span className="attendance-stat-value stat-present-text">{stats.present}</span>
          <span className="attendance-stat-label">Present</span>
        </div>
        <div className="attendance-stat-card">
          <span className="attendance-stat-value stat-absent-text">{stats.absent}</span>
          <span className="attendance-stat-label">Absent</span>
        </div>
        <div className="attendance-stat-card">
          <span className="attendance-stat-value stat-halfday-text">{stats.halfDay}</span>
          <span className="attendance-stat-label">Half Day</span>
        </div>
        <div className="attendance-stat-card">
          <span className="attendance-stat-value stat-leave-text">{stats.leave}</span>
          <span className="attendance-stat-label">Leave</span>
        </div>
      </div>

      <div className="attendance-toolbar">
        <div className="input-group search-input">
          <FaSearch className="input-icon" />
          <input
            type="text"
            placeholder="Search by name or ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {saving && <span className="saving-indicator">Saving...</span>}
      </div>

      <div className="attendance-table-wrapper">
        <table className="attendance-table">
          <thead>
            <tr>
              <th>Staff Member</th>
              <th>Role</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="3" className="attendance-empty-row">
                  Loading staff...
                </td>
              </tr>
            )}

            {!loading &&
              filteredStaff.map((member) => {
                const currentStatus = attendanceMap[member.id];
                return (
                  <tr key={member.id} className="attendance-row">
                    <td>
                      <div className="table-staff-name">
                        <div className="staff-avatar">{initials(member.name)}</div>
                        <div>
                          <strong>{member.name}</strong>
                          <span className="table-sub">{member.employee_code}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`role-tag ${ROLE_STYLES[member.role]?.cls}`}>
                        {member.role}
                      </span>
                    </td>
                    <td>
                      <div className="status-btn-group">
                        {STATUS_OPTIONS.map((opt) => (
                          <button
                            key={opt.key}
                            className={`status-btn ${opt.cls} ${
                              currentStatus === opt.key ? 'active' : ''
                            }`}
                            onClick={() => handleMark(member.id, opt.key)}
                          >
                            {opt.icon}
                            <span>{opt.label}</span>
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}

            {!loading && filteredStaff.length === 0 && (
              <tr>
                <td colSpan="3" className="attendance-empty-row">
                  No staff members found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Attendance;